import { scrollVelocity, subscribeToScroll } from './scrollLoop';

/**
 * Throttles photo resolution.
 *
 * Without this, flinging past the preload margin fires a fetch and a main-thread decode for
 * every tile crossed — dozens at once. The decode storm, not the observers, is what drops
 * frames during a fast scroll.
 *
 * Two rules:
 *  1. At most MAX_CONCURRENT resolutions in flight.
 *  2. Nothing new starts while the page is moving fast. Tiles the user is flinging past would
 *     be released before they were ever seen, so the work is pure waste. The queue drains the
 *     moment scrolling settles.
 */

const MAX_CONCURRENT = 6;

/** Pixels per frame above which we assume the user is travelling, not looking. */
const FLING_THRESHOLD_PX = 45;

/** Releases retired per frame. Enough to keep up, few enough not to block one. */
const RELEASES_PER_FRAME = 8;

interface Entry {
  start: () => void;
  cancelled: boolean;
}

let active = 0;
const pending: Entry[] = [];
const pendingReleases = new Set<() => void>();
let pumpSubscribed = false;

function pump() {
  if (scrollVelocity() > FLING_THRESHOLD_PX) return;

  while (active < MAX_CONCURRENT && pending.length > 0) {
    const entry = pending.shift();
    if (!entry || entry.cancelled) continue;
    entry.start();
  }

  // Chunked. Draining every deferred release at once produced a 269ms long task the moment
  // scrolling settled — each release revokes a URL and re-renders a tile, so a batch of 30
  // lands as one synchronous burst. The remainder rides the next frame.
  let budget = RELEASES_PER_FRAME;
  for (const release of pendingReleases) {
    if (budget-- <= 0) break;
    pendingReleases.delete(release);
    release();
  }
  if (pendingReleases.size > 0) requestAnimationFrame(pump);
}

/**
 * Defer teardown until the page settles. Unmounting an `<img>` mid-fling costs DOM mutation and
 * paint for a tile nobody is looking at, and the user often scrolls straight back.
 * Returns a canceller for when the tile comes back into range first.
 */
export function scheduleRelease(release: () => void): () => void {
  ensurePump();
  pendingReleases.add(release);
  pump();
  return () => pendingReleases.delete(release);
}

/** Keep pumping as the page moves so the queue drains as soon as a fling decelerates. */
function ensurePump() {
  if (pumpSubscribed) return;
  pumpSubscribed = true;
  subscribeToScroll(() => pump());
}

export interface ScheduledResolve<T> {
  promise: Promise<T>;
  /** Drop it if it hasn't started; a started fetch is left to finish and ignored. */
  cancel: () => void;
}

export function scheduleResolve<T>(task: () => Promise<T>): ScheduledResolve<T> {
  ensurePump();

  const entry: Entry = { cancelled: false, start: () => undefined };

  const promise = new Promise<T>((resolve, reject) => {
    entry.start = () => {
      active += 1;
      task()
        .then(resolve, reject)
        .finally(() => {
          active -= 1;
          pump();
        });
    };
  });

  pending.push(entry);
  pump();

  return {
    promise,
    cancel: () => {
      entry.cancelled = true;
    },
  };
}

/** For the dev perf HUD. */
export function resolveQueueStats(): { active: number; queued: number; deferredReleases: number } {
  return {
    active,
    queued: pending.filter((e) => !e.cancelled).length,
    deferredReleases: pendingReleases.size,
  };
}
